import { addDays, addMonths, addWeeks } from "date-fns";
import { isFixed } from "@/react/helpers/paywallCouponHelpers";
import { amountInUnits } from "@/react/helpers/paywalls/currencies";
import type {
  PaywallPrice,
  PaywallPriceFrequency,
} from "@circle-react/types/PaywallPrice";
import type { GeneratorParameters } from "../hooks/usePreviewMutation/fetchMockedData";

export const isCouponApplicable = (
  props?: GeneratorParameters<any>,
  price?: PaywallPrice,
) => {
  const paywallCoupon = props?.checkoutPreviewContext?.coupon_applied;

  if (!paywallCoupon || !price) {
    return false;
  }

  return (
    (paywallCoupon?.all_paywalls ||
      !!(paywallCoupon?.paywall_prices?.[props?.paywall?.id] || []).includes(
        price.id,
      )) &&
    !(
      paywallCoupon?.duration === "repeating" &&
      price?.interval_type === "onetime"
    )
  );
};

export const INTERVAL_TYPE_TO_PERIOD: Record<
  PaywallPriceFrequency | "onetime",
  [typeof addDays, number]
> = Object.freeze({
  onetime: [addDays, 0],
  monthly: [addMonths, 1],
  bimonthly: [addMonths, 2],
  quarterly: [addMonths, 3],
  biannually: [addMonths, 6],
  yearly: [addMonths, 12],
  weekly: [addWeeks, 1],
  biweekly: [addWeeks, 2],
  daily: [addDays, 1],
});

export const getRecurrentPrice = (price?: PaywallPrice): number =>
  Math.floor((price?.amount || 0) / (price?.installments_count || 1));

export const getDiscountAmount = (
  props: GeneratorParameters<any>,
  amount: number,
  price?: PaywallPrice,
) => {
  const coupon = props.checkoutPreviewContext?.coupon_applied;

  if (!coupon || !isCouponApplicable(props, price)) return 0;

  return (
    Math.floor(
      isFixed(coupon.type)
        ? amountInUnits(props.paywall?.currency, coupon.amount)
        : (coupon.amount / 100) * amount,
    ) * -1
  );
};

export const calculateAmountWithDiscount = (
  props: GeneratorParameters<any>,
  amount: number,
  price?: PaywallPrice,
) => Math.max(0, amount + getDiscountAmount(props, amount, price));
